import React, { useState, useEffect } from 'react'
import { useUser } from 'util/UserContext'
import { Navigate } from 'react-router-dom'
import { ChevronDown, ChevronUp } from 'lucide-react'
import api from 'config/axiosConfig'
import NotFoundPage from 'components/ResponsePages/404NotFoundPage'
import { AxiosError } from 'axios'
import ServerErrorPage from 'components/ResponsePages/500ServerErrorPage'
import { Button } from 'ui/Button'
import { formatDateWithoutTimezone } from 'config/dayjsConfig'
import { apiRoutes } from "util/apiRoutes";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from 'ui/Select'

interface AdminReportsProps {
    isAdmin: boolean
}

interface Report {
    id: number
    user_id: number
    entity_type: string
    entity_id: number
    reason: string
    description: string | null
    status: string
    decision_description: string | null
    created_at: string
    updated_at: string
}

interface RowProps {
    report: Report
    handleStatusChange: (reportId: number, newStatus: string) => void
    handleDecisionDescriptionChange: (
        reportId: number,
        newDescription: string
    ) => void
    handleSave: (reportId: number) => void
}

const REPORT_STATUSES = [
    'awaiting_review',
    'in_review',
    'report_valid',
    'report_dismissed',
]

const formatTimestamp = (timestamp: string): string => {
    const parsedDate = new Date(timestamp)
    return Number.isNaN(parsedDate.getTime())
        ? timestamp
        : formatDateWithoutTimezone(parsedDate)
}

const Row = ({
    report,
    handleStatusChange,
    handleDecisionDescriptionChange,
    handleSave,
}: RowProps) => {
    const [open, setOpen] = useState(false)
    const isDecisionEditable = report.status === 'in_review'

    return (
        <React.Fragment>
            <tr className="admin-reports-row border-b border-eoe-border-main dark:border-eoe-dark-border-main">
                <td className="admin-reports-cell px-3 py-2">{report.id}</td>
                <td className="admin-reports-cell px-3 py-2">{report.user_id}</td>
                <td className="admin-reports-cell px-3 py-2">{report.entity_type}</td>
                <td className="admin-reports-cell px-3 py-2">{report.entity_id}</td>
                <td className="admin-reports-cell px-3 py-2">{report.reason}</td>
                <td className="admin-reports-cell px-3 py-2">
                    <Select
                        value={report.status}
                        onValueChange={(value) => handleStatusChange(report.id, value)}
                    >
                        <SelectTrigger className="admin-reports-status-select h-8 min-w-[11rem] rounded-md border border-eoe-border-main dark:border-eoe-dark-border-main">
                            <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent className="admin-reports-status-select-content">
                            {REPORT_STATUSES.map((status) => (
                                <SelectItem key={status} value={status}>
                                    {status}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </td>
                <td className="admin-reports-cell px-3 py-2">
                    {formatTimestamp(report.created_at)}
                </td>
                <td className="admin-reports-cell px-3 py-2">
                    {formatTimestamp(report.updated_at)}
                </td>
                <td className="admin-reports-cell px-3 py-2">
                    <button
                        type="button"
                        aria-label="expand row"
                        className="admin-reports-expand-button inline-flex size-8 items-center justify-center rounded-full text-eoe-text-sub hover:bg-eoe-icon-hover dark:text-eoe-dark-text-sub dark:hover:bg-eoe-dark-icon-hover transition-colors"
                        onClick={() => setOpen(!open)}
                    >
                        {open ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
                    </button>
                </td>
                <td className="admin-reports-cell px-3 py-2">
                    <Button
                        size="sm"
                        className="admin-reports-save-button h-8 px-3 text-xs"
                        onClick={() => handleSave(report.id)}
                    >
                        Save
                    </Button>
                </td>
            </tr>
            <tr className={`admin-reports-details-row ${open ? '' : 'hidden'}`}>
                <td className="admin-reports-details-cell px-3 py-2" colSpan={10}>
                    <div className="admin-reports-details-panel rounded-md border border-eoe-border-main dark:border-eoe-dark-border-main bg-eoe-bg dark:bg-eoe-dark-bg-modal p-3 space-y-3">
                        <h3 className="admin-reports-details-heading text-base font-semibold text-eoe-text dark:text-eoe-dark-text">
                            Details
                        </h3>
                        <div className="admin-reports-description-group grid grid-cols-1 gap-1">
                            <span className="admin-reports-description-label text-xs font-semibold uppercase text-eoe-text-meta dark:text-eoe-dark-text-meta">
                                Description
                            </span>
                            <p className="admin-reports-description-value text-sm text-eoe-text dark:text-eoe-dark-text">
                                {report.description || 'No description provided.'}
                            </p>
                        </div>
                        <div className="admin-reports-decision-group grid grid-cols-1 gap-1">
                            <label
                                htmlFor={`report-decision-${report.id}`}
                                className="admin-reports-decision-label text-xs font-semibold uppercase text-eoe-text-meta dark:text-eoe-dark-text-meta"
                            >
                                Decision Description
                            </label>
                            <textarea
                                id={`report-decision-${report.id}`}
                                value={report.decision_description ?? ''}
                                onChange={(event) =>
                                    handleDecisionDescriptionChange(
                                        report.id,
                                        event.target.value
                                    )
                                }
                                disabled={!isDecisionEditable}
                                className="admin-reports-decision-textarea w-full min-h-[96px] rounded-md border border-eoe-border-main dark:border-eoe-dark-border-main bg-eoe-bg dark:bg-eoe-dark-bg text-eoe-text dark:text-eoe-dark-text p-2 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                            />
                        </div>
                    </div>
                </td>
            </tr>
        </React.Fragment>
    )
}

const AdminReports = ({ isAdmin }: AdminReportsProps) => {
    const { currentUser, awaitingUser } = useUser()
    const [reports, setReports] = useState<Report[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (!awaitingUser && isAdmin) {
            fetchReports()
        }
    }, [awaitingUser, isAdmin])

    const fetchReports = async () => {
        try {
            const response = await api.get(apiRoutes.reports.root)
            setReports(response.data)
            setLoading(false)
        } catch (error) {
            if (error instanceof AxiosError && error.response?.status === 404) {
                setError('404: Failed to fetch reports')
            } else {
                console.error('Error fetching reports:', error)
                setError('Failed to fetch reports')
            }
            setLoading(false)
        }
    }

    const handleStatusChange = (reportId: number, newStatus: string) => {
        setReports((currentReports) =>
            currentReports.map((report) =>
                report.id === reportId
                    ? { ...report, status: newStatus }
                    : report
            )
        )
    }

    const handleDecisionDescriptionChange = (
        reportId: number,
        newDescription: string
    ) => {
        setReports((currentReports) =>
            currentReports.map((report) =>
                report.id === reportId
                    ? { ...report, decision_description: newDescription }
                    : report
            )
        )
    }

    const handleSave = async (reportId: number) => {
        const report = reports.find((r) => r.id === reportId)
        if (!report) return

        try {
            await api.patch(apiRoutes.reports.byId(reportId), {
                status: report.status,
                decisionDescription: report.decision_description,
            })
            // Optionally, you can fetch the reports again to ensure the data is up-to-date
            await fetchReports()
        } catch (error) {
            console.error('Error updating report:', error)
            setError('Failed to update report')
        }
    }

    if (awaitingUser) {
        return (
            <div className="admin-reports-awaiting-user p-4 text-sm text-eoe-text dark:text-eoe-dark-text">
                Loading...
            </div>
        )
    }

    if (!currentUser || !currentUser.is_admin) {
        return <Navigate to="/" />
    }

    if (loading) {
        return (
            <div className="admin-reports-loading p-4 text-sm text-eoe-text dark:text-eoe-dark-text">
                Loading...
            </div>
        )
    }

    if (error) return error.match(/404/) ? <NotFoundPage displayStyle={`page`} errorMessage={error} buttonText={`Reload Page`} /> : <ServerErrorPage />

    return (
        <div className="admin-reports-table-container overflow-x-auto rounded-md border border-eoe-border-main dark:border-eoe-dark-border-main bg-eoe-bg dark:bg-eoe-dark-bg">
            <table className="admin-reports-table min-w-[1100px] w-full border-collapse text-left text-sm text-eoe-text dark:text-eoe-dark-text">
                <thead className="admin-reports-table-head bg-eoe-icon dark:bg-eoe-dark-icon">
                    <tr className="admin-reports-header-row border-b border-eoe-border-main dark:border-eoe-dark-border-main">
                        <th className="admin-reports-header-cell px-3 py-2 font-semibold">ID</th>
                        <th className="admin-reports-header-cell px-3 py-2 font-semibold">User ID</th>
                        <th className="admin-reports-header-cell px-3 py-2 font-semibold">Entity Type</th>
                        <th className="admin-reports-header-cell px-3 py-2 font-semibold">Entity ID</th>
                        <th className="admin-reports-header-cell px-3 py-2 font-semibold">Reason</th>
                        <th className="admin-reports-header-cell px-3 py-2 font-semibold">Status</th>
                        <th className="admin-reports-header-cell px-3 py-2 font-semibold">Created</th>
                        <th className="admin-reports-header-cell px-3 py-2 font-semibold">Updated</th>
                        <th className="admin-reports-header-cell px-3 py-2 font-semibold">Descriptions</th>
                        <th className="admin-reports-header-cell px-3 py-2 font-semibold">Actions</th>
                    </tr>
                </thead>
                <tbody className="admin-reports-table-body">
                    {reports.map((report) => (
                        <Row
                            key={report.id}
                            report={report}
                            handleStatusChange={handleStatusChange}
                            handleDecisionDescriptionChange={
                                handleDecisionDescriptionChange
                            }
                            handleSave={handleSave}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    )
}

export default AdminReports
